import Papa from 'papaparse';

export const MAX_IDENTIFIERS = 250;

export type IdentifierCsvSummary = {
  identifierColumn: string;
  uniqueCount: number;
  blankCount: number;
  duplicateCount: number;
};

export type IdentifierCsvCheck =
  | { isValid: true; summary: IdentifierCsvSummary }
  | { isValid: false; message: string };

type ParsedCsv = {
  rows: Record<string, string>[];
  fields: string[];
};

const parseCsv = (file: File): Promise<ParsedCsv> =>
  new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results: any) =>
        resolve({ rows: results?.data ?? [], fields: results?.meta?.fields ?? [] }),
      error: reject,
    });
  });

const findIdentifierColumn = (fields: string[]): string =>
  fields.find((field) => /identifier/i.test(field)) ?? fields[0];

export const checkIdentifierCsv = async (file: File): Promise<IdentifierCsvCheck> => {
  let parsed: ParsedCsv;
  try {
    parsed = await parseCsv(file);
  } catch {
    return { isValid: false, message: 'That file could not be read as a CSV.' };
  }

  const { rows, fields } = parsed;

  if (!fields.length || !fields[0]) {
    return { isValid: false, message: 'That CSV has no header row.' };
  }

  const identifierColumn = findIdentifierColumn(fields);
  const values = rows.map((row) => (row[identifierColumn] ?? '').trim());
  const populated = values.filter(Boolean);
  const uniqueCount = new Set(populated).size;

  if (!uniqueCount) {
    return {
      isValid: false,
      message: `No identifiers found in the "${identifierColumn}" column.`,
    };
  }

  if (uniqueCount > MAX_IDENTIFIERS) {
    return {
      isValid: false,
      message: `${uniqueCount} unique identifiers found, but only ${MAX_IDENTIFIERS} can be generated at a time. Please split the file.`,
    };
  }

  return {
    isValid: true,
    summary: {
      identifierColumn,
      uniqueCount,
      blankCount: values.length - populated.length,
      duplicateCount: populated.length - uniqueCount,
    },
  };
};
